/**
 * Story 13.15: External WebGL Preview Launcher
 *
 * Opens Unity WebGL preview in a separate browser tab to avoid embedding issues.
 * This eliminates:
 * - Background loading on non-Preview tabs
 * - Keyboard hijacking from SDL2 event listeners
 * - GLctx errors from cleanup race conditions
 * - Complex lazy mount/unmount patterns
 *
 * Based on DeepLinkModal pattern for VR Studio WebGL.
 */

import axios from "axios";
import apiUrls from "../../api";

export interface PreviewParams {
  projectId?: number;
  projectGroupId?: number;
  jwt: string;
  name?: string; // Name to display in window title (project or project-group name)
}

export interface PreviewResult {
  success: boolean;
  error?: string;
  popupBlocked?: boolean;
}

/**
 * Opens ProjectPreviewWebGL in a new browser tab with authentication and configuration.
 *
 * @param params - Preview parameters including projectId/projectGroupId and JWT
 * @returns PreviewResult indicating success/failure and specific error types
 *
 * @example
 * ```ts
 * // Open project-group preview
 * const result = await openPreviewInNewTab({
 *   projectGroupId: 123,
 *   jwt: currentJwt
 * });
 *
 * if (!result.success) {
 *   if (result.popupBlocked) {
 *     // Show popup blocker message
 *   } else {
 *     // Show generic error
 *   }
 * }
 * ```
 */
export async function openPreviewInNewTab(params: PreviewParams): Promise<PreviewResult> {
  const { projectId, projectGroupId, jwt, name } = params;

  // Validate parameters
  if (!jwt || jwt.trim() === "") {
    return { success: false, error: "JWT is required and must be non-empty" };
  }

  if (!projectId && !projectGroupId) {
    return { success: false, error: "Either projectId or projectGroupId must be provided" };
  }

  try {
    // Fetch signed JWT for WebGL app (same endpoint as DeepLinkModal for VR Studio)
    const response = await axios.post(apiUrls.auth.generateVrStudioJwt, null, {
      headers: { Authorization: `Bearer ${jwt}` },
    });
    const signedJwt = response.data;

    if (!signedJwt || signedJwt.trim() === "") {
      return { success: false, error: "JWT generation returned empty response" };
    }

    // Build preview URL with parameters
    const apiEndpoint = process.env.REACT_APP_API_ENDPOINT || "";

    // Validate API endpoint is non-empty
    if (!apiEndpoint || apiEndpoint.trim() === "") {
      return { success: false, error: "API endpoint is not configured" };
    }

    const url = new URL(window.location.origin + "/ProjectPreviewWebGL/index.html");

    // Add entity IDs
    if (projectGroupId) {
      url.searchParams.set("projectGroupId", String(projectGroupId));
    }
    if (projectId) {
      url.searchParams.set("projectId", String(projectId));
    }

    // Add authentication and config
    url.searchParams.set("token", signedJwt);
    url.searchParams.set("apiEndpoint", apiEndpoint);

    // Add name for window title if provided
    if (name) {
      url.searchParams.set("name", name);
    }

    // Open in new tab with security options
    // - Dynamic window name based on entity name (creates separate tabs for different entities)
    // - noopener prevents access to window.opener (security)
    // - noreferrer doesn't send referer header (privacy)
    const windowTarget = name
      ? `wixar-preview-${name.replace(/[^a-zA-Z0-9]/g, "_")}`
      : "wixar-preview";
    const previewWindow = window.open(url.toString(), windowTarget, "noopener,noreferrer");

    if (!previewWindow) {
      return {
        success: false,
        error: "Popup was blocked by browser",
        popupBlocked: true,
      };
    }

    return { success: true };
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : "Unknown error";
    return { success: false, error: `Failed to open preview: ${errorMessage}` };
  }
}
